import React from 'react';
import { Keyboard } from 'react-native';
import { List } from '@banhala/ui-kit';
import { type SuggestItem } from '../../../../constants';
import type { SuggestKeywordResponse } from 'shared/src/api/searchService/types';
import { SuggestRow } from './SuggestRow';
import { SearchType } from 'shared/src/api/enums';
import { SuggestType } from './SuggestType';
import { useLogSearchKeyword } from '../../hook/useLogSearchKeyword';
import { useOpenDeeplink } from 'shared/src/hooks';

const suggestTypeToSearchType: Record<SuggestType, SearchType> = {
  [SuggestType.QUERY]: SearchType.AUTO_COMPLETE_SEARCH_KEYWORD,
  [SuggestType.CATEGORY]: SearchType.AUTO_COMPLETE_CATEGORY,
  [SuggestType.BRAND]: SearchType.AUTO_COMPLETE_BRAND,
  [SuggestType.MARKET]: SearchType.AUTO_COMPLETE_MARKET,
  [SuggestType.EVENT]: SearchType.AUTO_COMPLETE_EVENT,
  [SuggestType.LICENSED_IP]: SearchType.AUTO_COMPLETE_LICENSED_IP,
  [SuggestType.OFFICIAL_MARKET]: SearchType.AUTO_COMPLETE_OFFICIAL_MARKET,
  [SuggestType.SERVICE]: SearchType.AUTO_COMPLETE_SERVICE,
  [SuggestType.DEEPLINK]: SearchType.AUTO_COMPLETE_DEEPLINK,
};

const suggestTypeToSnoKey: Record<SuggestType, string | null> = {
  [SuggestType.QUERY]: null,
  [SuggestType.CATEGORY]: 'CATEGORY_SNO',
  [SuggestType.BRAND]: 'BRAND_SNO',
  [SuggestType.MARKET]: 'MARKET_SNO',
  [SuggestType.EVENT]: 'EVENT_SNO',
  [SuggestType.LICENSED_IP]: 'LICENSED_IP_SNO',
  [SuggestType.OFFICIAL_MARKET]: 'MARKET_SNO',
  [SuggestType.SERVICE]: 'SERVICE_SNO',
  [SuggestType.DEEPLINK]: null,
};

export const SuggestList = ({
  query,
  data,
  onSelect,
}: {
  query: string;
  data: SuggestKeywordResponse;
  onSelect: (item: SuggestItem) => void;
}) => {
  const logSearchKeyword = useLogSearchKeyword();
  const { openDeeplink } = useOpenDeeplink();

  const linked = data.linked_suggesters ?? [];
  const queryItems = data.query_suggesters ?? [];

  const items = [...linked, ...queryItems];
  const linkedLength = linked.length;

  return (
    <List
      data={items}
      keyExtractor={(item, index) => `${item.type}-${index}`}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      renderItem={({ item, index }: { item: SuggestItem; index: number }) => (
        <SuggestRow
          item={item}
          queryIndex={index - linkedLength}
          onPress={async selectedItem => {
            const suggestType = selectedItem.type as SuggestType;
            const searchType = suggestType
              ? suggestTypeToSearchType[suggestType]
              : SearchType.AUTO_COMPLETE_SEARCH_KEYWORD;
            const snoKey = suggestType
              ? suggestTypeToSnoKey[suggestType]
              : null;

            logSearchKeyword({
              searchType,
              keyword: selectedItem.suggest,
              clickIndex: index,
              typedKeystroke: query,
              suggestedWordsCount: items.length,
              extraAnalytics:
                snoKey && selectedItem.type_sno
                  ? { [snoKey]: String(selectedItem.type_sno) }
                  : undefined,
            });

            Keyboard.dismiss();

            // iOS: 타입별 화면 이동
            switch (suggestType) {
              case SuggestType.MARKET:
                // 마켓 상세 화면으로 이동
                if (selectedItem.type_sno) {
                  openDeeplink(`ably://markets/${selectedItem.type_sno}`);
                }
                break;

              case SuggestType.QUERY:
                // 검색 실행
                onSelect(selectedItem);
                break;

              default:
                // 딥링크로 이동
                if (selectedItem.deeplink) {
                  openDeeplink(selectedItem.deeplink);
                }
                break;
            }
          }}
          showBottomLine={index === linkedLength - 1}
        />
      )}
      onScrollBeginDrag={() => {
        Keyboard.dismiss();
      }}
    />
  );
};

